import { useState, Fragment } from "react";
import { ChevronDown, ChevronRight, Download, Search, Wrench } from "lucide-react";
import { toast } from "sonner";
import { MATERIAL_CATEGORIES } from "./MaterialConstants";
import { exportToXlsx } from "./exportXlsx";

const NON_CONSTRUCTION = MATERIAL_CATEGORIES.filter((c) => c.id !== "construction");
const fieldCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary";
const labelCls = "block text-[10px] font-medium text-slate-600 mb-1 uppercase tracking-widest";
const inr = (v) => `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const buildUsage = (records, materials, equipmentList, f) => {
  const term = f.search.trim().toLowerCase();
  const map = {};
  records.forEach((r) => {
    if (!r.equipment_id || r.is_fuel_issue) return;
    const mat = materials.find((m) => m.id === r.material_id);
    const cat = r.category || mat?.category || "construction";
    if (cat === "construction") return;
    if (f.category !== "all" && cat !== f.category) return;
    if (f.from && r.date < f.from) return;
    if (f.to && r.date > f.to) return;
    const eq = equipmentList.find((e) => e.id === r.equipment_id);
    const eqName = eq ? eq.name : "Unknown machine";
    const eqReg = eq ? eq.equipment_id || eq.registration_number || "" : "";
    if (term && !`${eqName} ${eqReg}`.toLowerCase().includes(term)) return;
    const value = (r.quantity || 0) * (mat?.rate || 0);
    const m = (map[r.equipment_id] ||= { id: r.equipment_id, name: eqName, reg: eqReg, entries: [], value: 0 });
    m.entries.push({ ...r, material_name: mat?.name || "Unknown", unit: mat?.unit || "", rate: mat?.rate || 0, value, cat });
    m.value += value;
  });
  return Object.values(map).sort((a, b) => b.value - a.value);
};

const exportUsage = (usage, f, projectName) => {
  const rows = usage.flatMap((m) => m.entries.map((e) => ({
    machine: m.name, reg: m.reg, date: e.date, issue: e.issue_number, category: (MATERIAL_CATEGORIES.find((c) => c.id === e.cat) || {}).label,
    material: e.material_name, unit: e.unit, qty: e.quantity, rate: e.rate, value: e.value, issued_to: e.issued_to, purpose: e.purpose
  })));
  return exportToXlsx({
    fileName: `Machine_Usage_${f.from || "all"}_${f.to || "all"}.xlsx`,
    title: `Machine-Wise Spares & Consumables Usage${projectName ? ` - ${projectName}` : ""}`,
    subtitle: `Period: ${f.from || "start"} to ${f.to || "today"}  |  Machines: ${usage.length}`,
    sheetName: "Machine Usage",
    columns: [
      { header: "Machine", key: "machine", width: 22 }, { header: "Reg / ID", key: "reg", width: 16 }, { header: "Date", key: "date", width: 12 },
      { header: "Issue No.", key: "issue", width: 14 }, { header: "Category", key: "category", width: 20 }, { header: "Material", key: "material", width: 26 },
      { header: "Unit", key: "unit", width: 8 }, { header: "Qty", key: "qty", width: 10, numFmt: "#,##0.00" }, { header: "Rate (₹)", key: "rate", width: 12, numFmt: "#,##0.00" },
      { header: "Value (₹)", key: "value", width: 14, numFmt: "#,##0.00", total: true }, { header: "Issued To", key: "issued_to", width: 18 }, { header: "Purpose", key: "purpose", width: 22 }
    ],
    rows
  });
};

const MachineUsageTab = ({ outwardRecords, materials, equipmentList, projectName }) => {
  const [f, setF] = useState({ from: "", to: "", category: "all", search: "" });
  const [open, setOpen] = useState({});
  const usage = buildUsage(outwardRecords, materials, equipmentList, f);
  const total = usage.reduce((s, m) => s + m.value, 0);

  const onExport = async () => {
    if (!usage.length) return toast.error("Nothing to export");
    await exportUsage(usage, f, projectName);
    toast.success("Machine usage exported to Excel");
  };

  return (
    <div data-testid="machine-usage-tab">
      <div className="stat-card mb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div><label className={labelCls}>From Date</label><input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className={`${fieldCls} font-mono`} data-testid="machine-usage-from" /></div>
          <div><label className={labelCls}>To Date</label><input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className={`${fieldCls} font-mono`} data-testid="machine-usage-to" /></div>
          <div><label className={labelCls}>Category</label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={fieldCls} data-testid="machine-usage-category">
              <option value="all">Spares, Consumables & Misc</option>
              {NON_CONSTRUCTION.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Machine</label>
            <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} placeholder="Name or reg no." className={`${fieldCls} pl-8`} data-testid="machine-usage-search" /></div>
          </div>
          <button onClick={onExport} className="btn-outline flex items-center justify-center gap-2 h-[38px]" data-testid="machine-usage-export-btn"><Download className="w-4 h-4" /> Export Excel</button>
        </div>
        <div className="flex gap-6 mt-3 pt-3 border-t border-slate-200 text-sm text-slate-600" data-testid="machine-usage-summary">
          <span>Machines: <strong className="text-slate-900 font-mono">{usage.length}</strong></span>
          <span>Total Value: <strong className="text-slate-900 font-mono">{inr(total)}</strong></span>
        </div>
      </div>

      <div className="stat-card overflow-x-auto">
        <table className="w-full" data-testid="machine-usage-table">
          <thead><tr className="border-b-2 border-slate-300 text-xs uppercase tracking-widest text-slate-700">
            <th className="w-8" /><th className="text-left py-3 px-4 font-medium">Machine</th><th className="text-left py-3 px-4 font-medium">Reg / ID</th>
            <th className="text-right py-3 px-4 font-medium">Issues</th><th className="text-right py-3 px-4 font-medium">Total Value</th>
          </tr></thead>
          <tbody>
            {usage.map((m) => (
              <Fragment key={m.id}>
                <tr className="border-b border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen({ ...open, [m.id]: !open[m.id] })} data-testid={`machine-usage-row-${m.id}`}>
                  <td className="pl-3">{open[m.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                  <td className="py-3 px-4 text-sm font-medium">{m.name}</td>
                  <td className="py-3 px-4 text-sm font-mono">{m.reg || "-"}</td>
                  <td className="py-3 px-4 text-sm font-mono text-right">{m.entries.length}</td>
                  <td className="py-3 px-4 text-sm font-mono font-bold text-right">{inr(m.value)}</td>
                </tr>
                {open[m.id] && (
                  <tr className="bg-slate-50"><td colSpan={5} className="px-10 py-3">
                    <table className="w-full text-sm" data-testid={`machine-usage-detail-${m.id}`}>
                      <thead><tr className="text-[10px] uppercase tracking-widest text-slate-500"><th className="text-left py-1">Date</th><th className="text-left">Issue No.</th><th className="text-left">Material</th><th className="text-right">Qty</th><th className="text-right">Value</th><th className="text-left pl-4">Purpose</th></tr></thead>
                      <tbody>{m.entries.sort((a, b) => b.date.localeCompare(a.date)).map((e) => (
                        <tr key={e.id} className="border-t border-slate-200"><td className="py-1 font-mono">{e.date}</td><td className="font-mono">{e.issue_number}</td><td>{e.material_name}</td>
                          <td className="text-right font-mono">{e.quantity} {e.unit}</td><td className="text-right font-mono">{inr(e.value)}</td><td className="pl-4 text-slate-600">{e.purpose || "-"}</td></tr>
                      ))}</tbody>
                    </table>
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        {usage.length === 0 && (
          <div className="text-center py-12 text-slate-500" data-testid="machine-usage-empty">
            <Wrench className="w-14 h-14 mx-auto mb-3 text-slate-400" />
            No spares or consumables issued to machines for the selected filters.
          </div>
        )}
      </div>
    </div>
  );
};

export default MachineUsageTab;
